/**
 * Admin API Error Normalization
 * 
 * Standardizes API error handling across the admin dashboard.
 * Ensures no raw stack traces or internal objects are displayed to users.
 */

/**
 * Normalizes an API error to a consistent shape
 * @param {Error|Object} err - Error object from API call
 * @returns {Object} Normalized error: { status, code, message, detail, requestId, isTransient }
 */
export function normalizeApiError(err) {
  // Handle different error shapes
  const status = err.status || err.statusCode || err.response?.status || null;
  const code = err.code || err.errorCode || err.response?.data?.errorCode || null;
  const rawMessage = err.message || err.response?.data?.message || err.response?.data?.error || 'An error occurred';
  const detail = err.detail || err.response?.data?.detail || null;
  const requestId = err.requestId || err.response?.data?.requestId || err.response?.headers?.['x-request-id'] || null;
  
  // Determine if error is transient (retryable)
  const isTransient = status === 429 || status === 503 || status === 504 || 
                      (status >= 500 && status < 600) ||
                      code === 'RATE_LIMITED' || code === 'SERVICE_UNAVAILABLE';

  // Sanitize message - never expose stack traces or internal details
  let message = rawMessage;
  if (typeof message === 'string') {
    // Remove stack traces
    message = message.split('\n')[0].trim();
    // Remove internal error patterns
    message = message.replace(/at\s+.*/g, '').trim();
    message = message.replace(/Error:\s*/i, '').trim();
  } else {
    message = 'An error occurred';
  }

  // Map common error codes to user-friendly messages
  if (code === 'POLICY_BLOCKED') {
    message = 'This action is blocked by production safety policy';
  } else if (code === 'RATE_LIMITED' || status === 429) {
    message = 'Too many requests. Please wait a moment and try again.';
  } else if (status === 403) {
    message = 'You do not have permission to perform this action';
  } else if (status === 401) {
    message = 'Your session has expired. Please refresh the page.';
  } else if (status === 404) {
    message = 'The requested resource was not found';
  } else if (status === 500) {
    message = 'A server error occurred. Please try again later.';
  }

  return {
    status,
    code,
    message,
    detail,
    requestId,
    isTransient,
  };
}

/**
 * Gets a user-friendly message for a normalized error
 * @param {Object} normalized - Normalized error from normalizeApiError
 * @returns {string} User-friendly message
 */
export function userMessageForError(normalized) {
  if (!normalized) return 'An unexpected error occurred';

  let message = normalized.message || 'An error occurred';

  // Add retry-after info for rate limits
  if (normalized.status === 429 && normalized.detail?.retryAfter) {
    const seconds = normalized.detail.retryAfter;
    const minutes = Math.ceil(seconds / 60);
    message += ` Please wait ${minutes} minute${minutes !== 1 ? 's' : ''} before trying again.`;
  }

  // Add request ID for support
  if (normalized.requestId) {
    message += ` (Request ID: ${normalized.requestId.substring(0, 8)}...)`;
  }

  return message;
}

/**
 * Checks if an error is retryable
 * @param {Object} normalized - Normalized error
 * @returns {boolean} True if error is transient and can be retried
 */
export function isRetryableError(normalized) {
  return normalized?.isTransient === true;
}

